import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, Calendar, Shield, UserRoundCheck } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";

const Ajuda = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Central de Ajuda</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tudo o que você precisa saber para aproveitar ao máximo sua experiência na Live Stay
          </p>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Como Funciona</h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <Card className="text-center border shadow-soft">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Search className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">1. Busque</h3>
              <p className="text-sm text-muted-foreground">
                Escolha suas datas e veja as opções disponíveis nos nossos empreendimentos
              </p>
            </CardContent>
          </Card>

          <Card className="text-center border shadow-soft">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10 flex items-center justify-center">
                <Calendar className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">2. Reserve</h3>
              <p className="text-sm text-muted-foreground">Selecione o apartamento ideal e faça sua reserva online</p>
            </CardContent>
          </Card>

          <Card className="text-center border shadow-soft">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <UserRoundCheck className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">3. Aprovação</h3>
              <p className="text-sm text-muted-foreground">
                O proprietário irá entrar em contato para alinhar preços e demais informações, sua reserva é garantida
                após a aprovação.
              </p>
            </CardContent>
          </Card>

          <Card className="text-center border shadow-soft">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">4. Aproveite</h3>
              <p className="text-sm text-muted-foreground">Chegue e desfrute de toda infraestrutura do resort</p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Reservas */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold mb-6">Perguntas Frequentes - Reservas</h3>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left">Como faço para reservar um apartamento?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                É simples! Use nossa barra de busca na página inicial, selecione suas datas de check-in e check-out, o
                número de hóspedes e clique em "Buscar". Você verá todos os apartamentos disponíveis. Escolha o que mais
                gostar e siga as instruções para completar sua reserva.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left">Como funciona o pagamento?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                O Live Stay é um intermediário que conecta pessoas que procuram uma experiência única com quem pode
                oferecer essa experiência. A negociação e o pagamento são feitos diretamente com o proprietário do imóvel.
                O site facilita o encontro e a comunicação entre hóspedes e proprietários.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left">Qual é o horário de check-in e check-out?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                O check-in é realizado a partir das 15h e o check-out deve ser feito até às 12h. Caso precise de
                horários diferenciados, entre em contato conosco com antecedência para verificar disponibilidade (pode
                haver custos adicionais).
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left">Posso cancelar minha reserva?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Sim, o cancelamento pode ser feito de duas maneiras:
                <br /><br />
                <strong>1. Antes da aprovação do proprietário:</strong> Você pode cancelar diretamente pela plataforma
                antes que o proprietário aprove sua solicitação de reserva.
                <br /><br />
                <strong>2. Após a aprovação:</strong> Neste caso, entre em contato diretamente com o proprietário para
                negociar o cancelamento e quaisquer condições aplicáveis.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left">O que está incluso na diária?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Todas as diárias incluem acesso completo à infraestrutura do resort (piscinas, área de lazer, quadras
                esportivas), Wi-Fi, roupa de cama e banho, utensílios de cozinha completos e estacionamento. Serviços de
                limpeza e alimentação podem ser contratados separadamente.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contato */}
      <section className="py-16 container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ainda tem dúvidas?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Nossa equipe está pronta para ajudar você a planejar sua estadia perfeita
          </p>
          <a
            href="/fale-conosco"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
          >
            Entre em Contato
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ajuda;
